import { StrictMode, useEffect, useState } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import type { ReactElement } from 'react'
import { clearAuth, hasAuth } from './api/auth'
import { ApiError, api } from './api/client'
import type { Session } from './api/client'
import { SessionProvider } from './api/session'
import { Loading } from './components/common/Feedback'
import { AppLayout } from './AppLayout'
import { LoginPage } from './pages/LoginPage'
import { OAuthLoginPage } from './pages/OAuthLoginPage'

/** Wo die Anmeldung des OAuth-Ablaufs liegt — außerhalb der Oberfläche. */
const OAUTH_PREFIX = '/oauth/'
import { ExchangesPage } from './pages/ExchangesPage'
import { CommoditiesPage } from './pages/CommoditiesPage'
import { VocabularyPage } from './pages/VocabularyPage'
import { EventsPage } from './pages/EventsPage'
import { IndustriesPage } from './pages/IndustriesPage'
import { InstrumentsPage } from './pages/InstrumentsPage'
import { InstrumentDetailPage } from './pages/InstrumentDetailPage'
import { AssessmentsPage } from './pages/AssessmentsPage'
import { FxRatesPage } from './pages/FxRatesPage'
import { ImportsPage } from './pages/ImportsPage'
import { RssPage } from './pages/RssPage'
import { ImportDetailPage } from './pages/ImportDetailPage'
import { WatchlistPage } from './pages/WatchlistPage'
import { DepotPage } from './pages/DepotPage'
import { DepotsPage } from './pages/DepotsPage'
import { QuotesPage } from './pages/QuotesPage'
import { PriceHistoryPage } from './pages/PriceHistoryPage'
import { BuyTheDipPage } from './pages/BuyTheDipPage'
import { DailyStretchPage } from './pages/DailyStretchPage'
import { FeedPage } from './pages/FeedPage'
import { UebersichtPage } from './pages/UebersichtPage'
import { NewsPage } from './pages/NewsPage'
import { ResearchDetailPage } from './pages/ResearchDetailPage'
import { ResearchPage } from './pages/ResearchPage'
import { TradeIdeasPage } from './pages/TradeIdeasPage'
import { ComponentsPage } from './pages/ComponentsPage'
import { ZugaengePage } from './pages/ZugaengePage'
import './styles/main.scss'

/**
 * Ohne gültige Zugangsdaten zur Anmeldung.
 *
 * Geprüft wird gegen den SERVER und nicht nur gegen den Speicher: Ein Eintrag
 * im sessionStorage sagt nur, dass jemand einmal etwas eingetippt hat. Wären
 * die Daten inzwischen falsch — Kennwort in der Konfiguration geändert —,
 * stünde die Anwendung da und jeder Aufruf lieferte 401, ohne dass klar wäre,
 * warum.
 *
 * Die geladene Sitzung wird durchgegeben, statt sie wegzuwerfen: Die Ansichten
 * brauchen die Rechte, und ein zweiter Aufruf für dieselbe Antwort wäre
 * verschenkt.
 *
 * Das ist Bequemlichkeit und kein Schutz. Verbindlich geprüft wird am Service,
 * bei jedem Aufruf, serverseitig.
 */
function Protected({ children }: { children: ReactElement }) {
  const location = useLocation()
  const [state, setState] = useState<'checking' | 'ok' | 'rejected'>(
    hasAuth() ? 'checking' : 'rejected')
  const [session, setSession] = useState<Session | null>(null)

  useEffect(() => {
    if (state !== 'checking') return
    let current = true
    api.session()
      .then((answer) => {
        if (!current) return
        setSession(answer)
        setState('ok')
      })
      .catch((cause) => {
        if (!current) return
        if (cause instanceof ApiError && cause.status === 401) {
          // Die Daten im Speicher taugen nicht mehr — weg damit, sonst
          // scheitert jeder weitere Aufruf still.
          clearAuth()
          setState('rejected')
        } else {
          // Server nicht erreichbar: NICHT abmelden. Der Anmeldende hat nichts
          // falsch gemacht, und nach einem Neustart des Dienstes trägt
          // derselbe Header wieder. Die Sitzung bleibt dann unbekannt, und die
          // Ansichten überlassen die Rechteprüfung dem Server.
          setState('ok')
        }
      })
    return () => { current = false }
  }, [state])

  if (state === 'rejected') {
    return <Navigate to="/login" state={{ from: location }} replace />
  }
  if (state === 'checking') return <Loading>prüfe Anmeldung …</Loading>
  return <SessionProvider session={session}>{children}</SessionProvider>
}

/** Der Grundpfad des Routers — siehe die Erklärung unten. */
function grundpfad(): string {
  return window.location.pathname.startsWith(OAUTH_PREFIX)
    ? '/'
    : import.meta.env.BASE_URL
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    {/*
      `basename` haelt den Router davon ab, /watcher/ als Teil der Route zu
      lesen: Ohne ihn wuerde aus /watcher/news der Pfad "/watcher/news", und
      keine Route traefe. Der Wert kommt aus Vites `base` — damit steht er an
      einer Stelle und nicht an zweien.

      AUSSER FUER DIE OAUTH-ANMELDUNG. Sie liegt NEBEN der Oberflaeche, unter
      /oauth/login an der Wurzel — weil sie zum Autorisierungsserver gehoert
      und jeder sie erreichen muss, den ein Client hierher schickt. Die
      Oberflaeche selbst bleibt auf bekannte Netze beschraenkt.

      Dieselbe Anwendung, zwei Adressen: Der Router bekommt deshalb den einen
      oder den anderen Grundpfad, je nachdem, wo er aufgerufen wurde. Die
      Dateien kommen in beiden Faellen aus /watcher/assets/.
    */}
    <BrowserRouter basename={grundpfad()}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        {/* Die Anmeldung des OAuth-Ablaufs. OHNE `Protected`:
            Wer hierher kommt, ist gerade NICHT angemeldet — das ist
            der Zweck der Seite. */}
        <Route path="/oauth/login" element={<OAuthLoginPage />} />
        <Route element={<Protected><AppLayout /></Protected>}>
          <Route path="/instruments" element={<InstrumentsPage />} />
          <Route path="/exchanges" element={<ExchangesPage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/commodities" element={<CommoditiesPage />} />
          <Route path="/vocabulary" element={<VocabularyPage />} />
          <Route path="/fx-rates" element={<FxRatesPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/instruments/:id" element={<InstrumentDetailPage />} />
          <Route path="/watchlist" element={<WatchlistPage />} />
          <Route path="/depots" element={<DepotsPage />} />
          <Route path="/depots/:id" element={<DepotPage />} />
          <Route path="/assessments" element={<AssessmentsPage />} />
          <Route path="/quotes" element={<QuotesPage />} />
          <Route path="/price-history" element={<PriceHistoryPage />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/research" element={<ResearchPage />} />
          <Route path="/rss" element={<RssPage />} />
          <Route path="/trade-ideas" element={<TradeIdeasPage />} />
          <Route path="/research/:id" element={<ResearchDetailPage />} />
          <Route path="/reports/buy-the-dip" element={<BuyTheDipPage />} />
          <Route path="/reports/daily-stretch"
                 element={<DailyStretchPage />} />
          <Route path="/" element={<UebersichtPage />} />
          <Route path="/feed" element={<FeedPage />} />
          <Route path="/components" element={<ComponentsPage />} />
          <Route path="/admin/imports" element={<ImportsPage />} />
          <Route path="/admin/imports/:id"
                 element={<ImportDetailPage />} />
          <Route path="/admin/oauth" element={<ZugaengePage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  </StrictMode>,
)
